import React, { useState, useRef, useEffect } from 'react';
import { AiOutlineUpload, AiOutlineUser, AiOutlineCreditCard, AiOutlineSetting, AiOutlineFileText, AiOutlineLogout, AiOutlineRight, AiOutlineMenu, AiOutlineShareAlt, AiOutlineDown, AiOutlineWarning, AiOutlineDownload } from 'react-icons/ai';
import { IoArrowBack } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { User } from 'firebase/auth';
import { useCampus } from '../contexts/CampusContext';
import useConfig from '../config';
import { uiText } from '../config/ui';

interface NavbarProps {
  hideNavbar: boolean;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  importButtonRef: React.RefObject<HTMLButtonElement>;
  exportButtonRef: React.RefObject<HTMLButtonElement>;
  setIsImportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsExportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isImportModalOpen: boolean;
  isExportModalOpen: boolean;
  user: User | null;
  onReset: () => void;
}

const Navbar: React.FC<NavbarProps> = ({
  hideNavbar,
  currentPage,
  setCurrentPage,
  importButtonRef,
  exportButtonRef,
  setIsImportModalOpen,
  setIsExportModalOpen,
  isImportModalOpen,
  isExportModalOpen,
  user,
  onReset
}) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCampusOpen, setIsCampusOpen] = useState(false);
  const { campus, setCampus } = useCampus();
  const [selectedCampus, setSelectedCampus] = useState(() => {
    const savedCampus = localStorage.getItem('ffcs_planner_campus');
    return savedCampus || 'Vellore';
  });
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [pendingCampus, setPendingCampus] = useState<string | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const campusRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();
  const facultyListText = uiText.navbar.facultyList[campus.toLowerCase() as keyof typeof uiText.navbar.facultyList];

  useEffect(() => {
    const savedCampus = localStorage.getItem('ffcs_planner_campus');
    if (savedCampus === 'Chennai') {
      setCampus('Chennai');
    } else {
      setCampus('Vellore');
    }
  }, [setCampus]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (campusRef.current && !campusRef.current.contains(event.target as Node)) {
        setIsCampusOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsProfileOpen(false);
  };

  const handleCampusChange = (newCampus: 'Vellore' | 'Chennai' | 'AP') => {
    // Check if there are any courses in localStorage
    const savedData = localStorage.getItem('ffcs_planner_data');
    const hasCourses = savedData ? JSON.parse(savedData).courses.length > 0 : false;

    if (hasCourses) {
      setPendingCampus(newCampus);
      setIsCampusOpen(false);
      setIsResetModalOpen(true);
    } else {
      localStorage.setItem('ffcs_planner_campus', newCampus);
      setSelectedCampus(newCampus);
      setCampus(newCampus);
      setIsCampusOpen(false);
    }
  };

  const confirmCampusChange = () => {
    if (pendingCampus) {
      localStorage.setItem('ffcs_planner_campus', pendingCampus);
      setSelectedCampus(pendingCampus);
      setCampus(pendingCampus as 'Vellore' | 'Chennai' | 'AP');
      onReset(); // This will clear all data and reload the page
      setPendingCampus(null);
    }
    setIsResetModalOpen(false);
  };

  const displayName = user?.displayName || 'User';
  const email = user?.email || 'No email provided';

  return (
    <>
      <nav className={`bg-white fixed w-full z-50 ${hideNavbar ? 'hidden' : ''}`}>
        <div className="h-[4.75rem] sm:h-[5.5rem] w-full flex items-center border-b border-gray-200">
          <div className="w-full px-3 sm:px-6 md:px-8 lg:px-20 flex items-center justify-between transition-all duration-300">
            {/* Left side - Conditional display */}
            <div className="flex items-center space-x-4 sm:space-x-5">
              {currentPage === 'faculty' ? (
                <button
                  onClick={() => setCurrentPage('dashboard')}
                  className="flex items-center text-base sm:text-lg lg:text-xl font-semibold text-gray-800 hover:text-blue-600 transition-colors"
                >
                  <IoArrowBack className="mr-2" />
                  <span>Back to Dashboard</span>
                </button>
              ) : (
                <button
                  onClick={() => setCurrentPage('dashboard')}
                  className="flex items-center space-x-4 sm:space-x-5"
                >
                  <img src="/favicon.png" alt="FFCS Logo" className="h-9 w-9 sm:h-11 sm:w-11 lg:h-[3.25rem] lg:w-[3.25rem]" />
                  <div className="flex flex-col items-start justify-center cursor-pointer">
                    {/* Campus Selector Dropdown */}
                    <div className="relative" ref={campusRef}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent parent button's onClick
                          setIsCampusOpen(!isCampusOpen);
                        }}
                        className="flex items-center space-x-1.5 text-base sm:text-lg lg:text-xl text-gray-900 font-semibold hover:text-gray-700 transition-colors h-8 group"
                      >
                        <span className="inline-flex items-center">{selectedCampus} Campus</span>
                        <AiOutlineDown className={`w-5 h-5 transition-transform stroke-[5] ${isCampusOpen ? 'rotate-180' : ''} relative top-[1px]`} />

                      </button>
                      {isCampusOpen && (
                        <div className="absolute left-0 mt-1 w-52 bg-white rounded-md shadow-lg border border-gray-200 py-1.5 z-50">
                          <button
                            onClick={() => handleCampusChange('Vellore')}
                            className={`w-full px-5 py-2.5 text-left text-base hover:bg-gray-100 ${selectedCampus === 'Vellore' ? 'text-gray-800 font-semibold' : 'text-gray-600 font-medium'}`}
                          >
                            Vellore Campus
                          </button>
                          <button
                            onClick={() => handleCampusChange('Chennai')}
                            className={`w-full px-5 py-2.5 text-left text-base hover:bg-gray-100 ${selectedCampus === 'Chennai' ? 'text-gray-800 font-semibold' : 'text-gray-600 font-medium'}`}
                          >
                            Chennai Campus
                          </button>
                          <button
                            onClick={() => handleCampusChange('AP')}
                            className={`w-full px-5 py-2.5 text-left text-base hover:bg-gray-100 ${selectedCampus === 'AP' ? 'text-gray-800 font-semibold' : 'text-gray-600 font-medium'}`}
                          >
                            AP Campus
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )}
            </div>

            {/* Right side - Import and Profile */}
            <div className="flex items-center">
              {currentPage === 'dashboard' && (
                <button
                  onClick={() => setCurrentPage('faculty')}
                  className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 hover:text-blue-600 transition-colors mr-4 flex items-center group"
                >
                  {facultyListText}
                  <AiOutlineRight className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 w-4 h-4 stroke-[5]" />
                </button>
              )}
              <div className="flex items-center space-x-0.5">
                <button
                  ref={importButtonRef}
                  onClick={() => {
                    setIsImportModalOpen(prev => !prev)
                    setIsExportModalOpen(false)
                  }}
                  className={`transition-colors p-2 ${isImportModalOpen ? 'text-black' : 'text-gray-700 hover:text-gray-900'}`}
                  title="Import data from CSV"
                >
                  <AiOutlineUpload className="h-[1.375rem] w-[1.375rem] sm:h-[1.625rem] sm:w-[1.625rem] lg:h-[1.875rem] lg:w-[1.875rem]" />
                </button>

                <button
                  ref={exportButtonRef}
                  onClick={() => {
                    setIsExportModalOpen(prev => !prev)
                    setIsImportModalOpen(false)
                  }}
                  className={`transition-colors p-2 ${isExportModalOpen ? 'text-black' : 'text-gray-700 hover:text-gray-900'}`}
                  title="Share or export data"
                >
                  <AiOutlineDownload className="h-[1.375rem] w-[1.375rem] sm:h-[1.625rem] sm:w-[1.625rem] lg:h-[1.875rem] lg:w-[1.875rem]" />
                </button>
              </div>

              {/* Add minimal spacing between share and profile */}
              <div className="w-1"></div>

              {user && (
                <div className="relative ml-2 sm:ml-3" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`h-11 w-11 sm:h-[3.25rem] sm:w-[3.25rem] rounded-full bg-gray-200 flex items-center justify-center text-gray-600 transition-colors
                      ${isProfileOpen ? 'ring-2 ring-offset-2 ring-gray-700' : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-700'}`}
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="User Avatar" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover" />
                    ) : (
                      <AiOutlineUser className="h-6 w-6 sm:h-7 sm:w-7" />
                    )}
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-[280px] sm:w-96 bg-white rounded-xl shadow-xl z-20 border border-gray-300 p-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <div className="bg-white rounded-lg">
                        <div className="px-4 sm:px-6 py-6 sm:py-8 border-b border-gray-200">
                          <div className="flex items-center">
                            {user.photoURL ? (
                              <img src={user.photoURL} alt="User Avatar" className="h-24 w-24 sm:h-36 sm:w-36 rounded-full mr-6 sm:mr-8 shrink-0 object-cover" />
                            ) : (
                              <div className="h-24 w-24 sm:h-36 sm:w-36 rounded-full mr-6 sm:mr-8 shrink-0 bg-gray-200 flex items-center justify-center">
                                <AiOutlineUser className="h-12 w-12 sm:h-20 sm:w-20 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="text-base sm:text-lg font-semibold text-gray-800">{displayName}</p>
                              <p className="text-sm text-gray-500 break-all font-medium">{email}</p>
                            </div>
                          </div>
                        </div>
                        <ul>
                          <li 
                            className="flex items-center justify-center px-4 sm:px-6 py-3 hover:bg-gray-100 cursor-pointer text-red-600"
                            onClick={handleLogout}
                          >
                            <AiOutlineLogout className="h-5 w-5 mr-3" />
                            <span className="text-sm font-medium">Logout</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Reset Warning Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4 text-yellow-500">
              <AiOutlineWarning className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-center mb-4">Switch Campus</h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to switch to {pendingCampus}? This will reset all your current data and cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setIsResetModalOpen(false);
                  setPendingCampus(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCampusChange}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 font-medium transition-colors"
              >
                Switch Campus
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar; 
